import React, { useEffect, useRef } from "react"

import {
  Container,
  TextContainer,
  TitleRow,
  Title,
  Text,
  Cards,
  Card,
  CardImage,
  CardTop,
  CardName,
  ClassTitlesWrapper,
  CardClassTitle,
  CardRow,
  CardRowTrack,
} from "./styles"

const HCC_ID = 152

const classIdMap = {
  138: { name: "FUNCTIONAL NUTRITION+", color: "#227298" },
  // 139: { name: "HEALTH COACHING+", color: "#E03015" },
  [HCC_ID]: { name: "HEALTH COACHING+", color: "#E03015" },
  157: { name: "PERI/MENOPAUSE+", color: "#A8146C" },
}

const ANIMATION_DURATION_PER_CARD = 4 // seconds per card
const SCROLL_SCALE = 0.45 // scroll speed multiplier
const TOUCH_SCALE = 0.8 // touch scroll speed multiplier

// Calculate animation duration to maintain consistent visual speed
// Uses unit width (the width of one set of cards) to calculate duration
const calculateAnimationDuration = (unitWidth, viewportWidth, track) => {
  // Get card width from DOM or use fallback
  const firstCard = track?.querySelector('[role="listitem"]')
  const cardWidth = firstCard ? firstCard.offsetWidth : 176 // Fallback to desktop default

  // Guard against invalid viewport width (e.g., row mounted offscreen)
  if (!viewportWidth || viewportWidth <= 0) {
    // Fallback: calculate duration based on unit width and card width
    const cardsInUnit = unitWidth / cardWidth
    return Math.round(cardsInUnit * ANIMATION_DURATION_PER_CARD * 1000)
  }

  // Calculate how many cards fit in viewport
  const cardsPerViewport = viewportWidth / cardWidth

  // Duration to scroll one viewport worth of cards at consistent speed
  const durationPerViewport = cardsPerViewport * ANIMATION_DURATION_PER_CARD * 1000

  // Scale to actual unit width being animated
  return Math.round((unitWidth / viewportWidth) * durationPerViewport)
}

const PeopleScrollableGrid = ({ items, title, subtitle, showClassTags }) => {
  const rowRefs = useRef([])
  const trackRefs = useRef([])

  // Transform data to arrange in correct order (top-to-bottom, then left-to-right)
  const arrangeDataInOrder = (dataItems) => {
    const arranged = []
    const rowCount = 2

    for (let col = 0; col < Math.ceil(dataItems.length / rowCount); col += 1) {
      for (let row = 0; row < rowCount; row += 1) {
        const index = col * rowCount + row
        if (index < dataItems.length) {
          arranged.push(dataItems[index])
        }
      }
    }

    return arranged
  }

  const arrangedItems = arrangeDataInOrder(items)

  const firstRow = arrangedItems.filter((_, index) => index % 2 === 0)
  const secondRow = arrangedItems.filter((_, index) => index % 2 === 1)
  const rows = [firstRow, secondRow]

  // Marquee animation logic - written by AI
  useEffect(() => {
    const containers = rowRefs.current
    const tracks = trackRefs.current

    const cleanups = containers.map((container, idx) => {
      const el = container
      const track = tracks[idx]
      if (!el || !track) return () => {}

      const getUnitWidth = () => track.scrollWidth / 3
      const hasOverflow = track.scrollWidth > el.clientWidth
      if (!getUnitWidth() || !hasOverflow) return () => {}

      // Check user's motion preferences
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      if (prefersReducedMotion) return () => {}

      const direction = idx === 0 ? "left" : "right"

      const createKeyframes = (unitWidth) => [
        { transform: "translate3d(0, 0, 0)" },
        { transform: `translate3d(-${Math.round(unitWidth)}px, 0, 0)` },
      ]

      const unitWidthPx = getUnitWidth()
      const keyframes = createKeyframes(unitWidthPx)
      const durationMs = calculateAnimationDuration(unitWidthPx, el.clientWidth, track)

      const animation = track.animate(keyframes, {
        duration: durationMs,
        iterations: Infinity,
        easing: "linear",
        direction: direction === "right" ? "reverse" : "normal",
      })

      // start centered
      el.scrollLeft = getUnitWidth()

      let isInteractive = false
      let pausedByVisibility = false
      let lastInteractionWasMouse = false

      const setInteractiveTrue = () => {
        isInteractive = true
        if (animation) animation.pause()
      }

      const setInteractiveFalse = () => {
        isInteractive = false
        // only resume if nothing inside retains focus
        const active = document.activeElement
        const hasFocusInside = active && el.contains(active)
        if (!hasFocusInside && animation) animation.play()
      }

      const onScroll = () => {
        if (isInteractive || pausedByVisibility) return
        let sc = el.scrollLeft
        const unit = getUnitWidth()
        const minBound = unit * 0.5
        const maxBound = unit * 1.5
        // Normalize position to middle band to avoid blank gaps
        if (sc < minBound || sc > maxBound) {
          // shift by whole units until within bounds
          // guard against extremely large deltas
          let guard = 0
          while (sc < minBound && guard < 4) {
            sc += unit
            guard += 1
          }
          while (sc > maxBound && guard < 8) {
            sc -= unit
            guard += 1
          }
          el.scrollLeft = sc
        }
      }

      const onWheel = (evt) => {
        if (!isInteractive) return

        const { deltaX, deltaY } = evt
        // Only handle when horizontal scrolling; ignore vertical scrolling
        if (Math.abs(deltaX) <= Math.abs(deltaY) || deltaX === 0) return
        evt.preventDefault()
        el.scrollLeft += deltaX * SCROLL_SCALE
        // wrap inside wheel interaction to prevent blank gaps mid-drag
        const unit = getUnitWidth()
        const minBound = unit * 0.5
        const maxBound = unit * 1.5
        if (el.scrollLeft < minBound) {
          el.scrollLeft += unit
        } else if (el.scrollLeft > maxBound) {
          el.scrollLeft -= unit
        }
      }

      const onMouseEnter = () => {
        animation.pause()
      }

      el.addEventListener("scroll", onScroll, { passive: true })
      el.addEventListener("wheel", onWheel, { passive: false })
      el.addEventListener("mouseenter", onMouseEnter)

      const onMouseLeave = () => {
        onScroll() // normalize after interaction
        animation.play()
      }
      el.addEventListener("mouseleave", onMouseLeave)

      //  Prevent focus from mouse clicks on cards
      let mouseInteractionTimeout = null
      const onMouseDown = (e) => {
        const targetCard = e.target.closest('[role="listitem"]')
        if (targetCard) {
          e.preventDefault()
        }
        lastInteractionWasMouse = true
        // Reset flag after a short delay to allow for keyboard interactions
        if (mouseInteractionTimeout) {
          clearTimeout(mouseInteractionTimeout)
        }
        mouseInteractionTimeout = setTimeout(() => {
          lastInteractionWasMouse = false
        }, 100)
      }

      const onFocusIn = (e) => {
        // Only pause animation and scroll into view for keyboard navigation
        // Ignore focus from mouse clicks to allow smooth scrolling to continue
        if (lastInteractionWasMouse) {
          return
        }

        setInteractiveTrue()
        const targetEl = e && e.target ? e.target.closest('[role="listitem"]') || e.target : null
        if (targetEl && el.contains(targetEl)) {
          const containerRect = el.getBoundingClientRect()
          const targetRect = targetEl.getBoundingClientRect()
          const padding = 20
          if (targetRect.left < containerRect.left + padding) {
            el.scrollLeft -= containerRect.left + padding - targetRect.left
          } else if (targetRect.right > containerRect.right - padding) {
            el.scrollLeft += targetRect.right - (containerRect.right - padding)
          }
        }
      }

      const onFocusOut = () => {
        setInteractiveFalse()
        onScroll() // normalize after interaction
      }

      el.addEventListener("mousedown", onMouseDown)
      el.addEventListener("focusin", onFocusIn)
      el.addEventListener("focusout", onFocusOut)

      let lastTouchX = 0
      let lastTouchY = 0

      const onTouchStart = (e) => {
        setInteractiveTrue()
        if (e.touches && e.touches.length) {
          lastTouchX = e.touches[0].clientX
          lastTouchY = e.touches[0].clientY
        }
      }

      const onTouchMove = (e) => {
        if (!isInteractive || !e.touches || !e.touches.length) return

        const x = e.touches[0].clientX
        const y = e.touches[0].clientY
        const dx = lastTouchX - x
        const dy = Math.abs(lastTouchY - y)
        const absDx = Math.abs(dx)

        // Only intercept when horizontal intent is stronger
        if (absDx > dy) {
          e.preventDefault()
          el.scrollLeft += dx * TOUCH_SCALE
          lastTouchX = x
          lastTouchY = y
          // wrap during touch interaction to avoid blank gaps
          const unit = getUnitWidth()
          const minBound = unit * 0.5
          const maxBound = unit * 1.5
          if (el.scrollLeft < minBound) {
            el.scrollLeft += unit
          } else if (el.scrollLeft > maxBound) {
            el.scrollLeft -= unit
          }
        } else {
          // allow vertical scroll; update anchors
          lastTouchX = x
          lastTouchY = y
        }
      }

      const onTouchEnd = () => {
        setInteractiveFalse()
        onScroll() // normalize after interaction
      }

      el.addEventListener("touchstart", onTouchStart, { passive: false })
      el.addEventListener("touchmove", onTouchMove, { passive: false })
      el.addEventListener("touchend", onTouchEnd)
      el.addEventListener("touchcancel", onTouchEnd)

      let resizeObserver
      let resizeTimeout
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          // Debounce resize updates to avoid excessive recalculations
          if (resizeTimeout) {
            clearTimeout(resizeTimeout)
          }
          resizeTimeout = setTimeout(() => {
            // Recalculate animation if track size changes
            const newUnitWidth = getUnitWidth()

            if (animation && animation.effect) {
              animation.effect.setKeyframes(createKeyframes(newUnitWidth))
              const newDuration = calculateAnimationDuration(newUnitWidth, el.clientWidth, track)
              animation.effect.updateTiming({ duration: newDuration })
            }
          }, 300)
        })
        resizeObserver.observe(track)
      }

      // Pause marquee when offscreen to save CPU/GPU
      let intersectionObserver
      if (typeof window !== "undefined" && "IntersectionObserver" in window) {
        intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries && entries[0]
            if (!entry) return
            if (entry.isIntersecting) {
              pausedByVisibility = false
              const active = document.activeElement
              const hasFocusInside = active && el.contains(active)
              if (!isInteractive && !hasFocusInside && animation) {
                animation.play()
              }
            } else {
              pausedByVisibility = true
              if (animation) {
                animation.pause()
              }
            }
          },
          { root: null, threshold: 0.1 },
        )
        intersectionObserver.observe(el)
      }

      return () => {
        el.removeEventListener("scroll", onScroll)
        el.removeEventListener("wheel", onWheel)
        el.removeEventListener("mouseenter", onMouseEnter)
        el.removeEventListener("mouseleave", onMouseLeave)
        el.removeEventListener("mousedown", onMouseDown)
        el.removeEventListener("focusin", onFocusIn)
        el.removeEventListener("focusout", onFocusOut)
        el.removeEventListener("touchstart", onTouchStart)
        el.removeEventListener("touchmove", onTouchMove)
        el.removeEventListener("touchend", onTouchEnd)
        el.removeEventListener("touchcancel", onTouchEnd)

        clearTimeout(mouseInteractionTimeout)
        clearTimeout(resizeTimeout)

        if (animation) animation.cancel()
          
        if (resizeObserver) resizeObserver.disconnect()
        if (intersectionObserver) intersectionObserver.disconnect()
      }
    })

    return () => {
      cleanups.forEach((dispose) => dispose && dispose())
    }
  }, [items])

  return (
    <Container>
      <TextContainer>
        <TitleRow>
          <Title dangerouslySetInnerHTML={{ __html: title }} />
        </TitleRow>
        {subtitle && <Text dangerouslySetInnerHTML={{ __html: subtitle }} />}
      </TextContainer>

      <Cards
        id="collective-cards"
        role="list"
        aria-label="Collective member cards"
        showClassTags={showClassTags}
      >
        {rows.map((row, rowIndex) => {
          const direction = rowIndex === 0 ? "left" : "right"
          const tripled = [...row, ...row, ...row]

          return (
            <CardRow
              key={`row-${row[0]?.id || row[0]?.name || rowIndex}`}
              showClassTags={showClassTags}
              ref={(el) => {
                rowRefs.current[rowIndex] = el
              }}
            >
              <CardRowTrack
                direction={direction}
                ref={(el) => {
                  trackRefs.current[rowIndex] = el
                }}
              >
                {tripled.map((item, idx) => {
                  const { name, image, id, classes } = item
                  const imageUrl = image && image[0]?.secure_url

                  let classTags = showClassTags
                    ? (classes?.filter((cls) => classIdMap[cls.classId]) || []).map(
                        (cls) => classIdMap[cls.classId],
                      )
                    : []

                  if (classTags.length > 1) {
                    // Temporary solution for showing only one class tag
                    // use HCC tag if present in the classTags
                    classTags = classTags.some((tag) => tag.name === classIdMap[HCC_ID].name)
                      ? [classIdMap[HCC_ID]]
                      : classTags.slice(0, 1)
                  }

                  const isDuplicate = idx < row.length || idx >= row.length * 2
                  const key = id || name ? `${id || name}-${idx}` : `${rowIndex}-${idx}`

                  return (
                    <Card
                      key={key}
                      role="listitem"
                      tabIndex={isDuplicate ? -1 : 0}
                      aria-hidden={isDuplicate}
                    >
                      <CardTop>
                        <CardImage
                          baseUrl={imageUrl}
                          alt={name}
                          images={[
                            {
                              transforms: "c_fill,g_auto,w_176,h_176,q_90,f_auto,fl_lossy",
                              screenSize: 992,
                            },
                            {
                              transforms: "c_fill,g_auto,w_145,h_145,q_90,f_auto,fl_lossy",
                              screenSize: 768,
                            },
                            {
                              transforms: "c_fill,g_auto,w_95,h_95,q_90,f_auto,fl_lossy",
                            },
                          ]}
                          lazy
                        />
                        <CardName>{name}</CardName>
                      </CardTop>

                      {classTags && showClassTags && (
                        <ClassTitlesWrapper>
                          {classTags.map((tag) => (
                            <CardClassTitle key={tag.name} color={tag.color}>
                              {tag.name}
                            </CardClassTitle>
                          ))}
                        </ClassTitlesWrapper>
                      )}
                    </Card>
                  )
                })}
              </CardRowTrack>
            </CardRow>
          )
        })}
      </Cards>
    </Container>
  )
}

export default PeopleScrollableGrid
